import { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { shipmentService } from '../../services/shipmentService.js';
import { mlSheetService } from '../../services/mlSheetService.js';

const money = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return `₹${Math.round(num).toLocaleString('en-IN')}`;
};

const hasText = (value) => {
  if (value == null) return false;
  const text = String(value).trim();
  return text !== '' && text.toLowerCase() !== 'null' && text !== '—';
};

const hasAmount = (value) => Number(value) > 0;

const formatWhen = (value) => {
  if (!hasText(value)) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CHARGE_FIELDS = [
  ['freight_charges', 'Freight'],
  ['handling_charges', 'Handling'],
  ['loading_charges', 'Loading'],
  ['unloading_charges', 'Unloading'],
  ['delivery_charges', 'Delivery'],
  ['door_pickup_charges', 'Door Pickup'],
  ['docket_fee', 'Docket'],
  ['late_charges', 'Late'],
  ['insurance_charges', 'Insurance'],
  ['toll_charges', 'Toll'],
  ['fuel_surcharge', 'Fuel'],
  ['packing_charges', 'Packing'],
  ['other_charges', 'Other'],
  ['discount', 'Discount'],
  ['sgst_amount', 'SGST'],
  ['cgst_amount', 'CGST'],
  ['igst_amount', 'IGST'],
];

const paymentBucket = (shipment) => {
  const mode = String(shipment?.mode_of_payment || '').toLowerCase();
  const total = Number(shipment?.total_amount || 0);
  if (shipment?.shipment_client_id || ['credit', 'on_account', 'onaccount', 'on account'].includes(mode)) {
    return 'on_account';
  }
  if (mode.includes('to_pay') || mode === 'topay' || mode === 'to pay') return 'to_pay';
  return 'paid';
};

const locationName = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.location || value.hub_name || value.name || '';
};

function Field({ label, value }) {
  if (!hasText(value)) return null;
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 m-0">{label}</p>
      <p className="text-sm font-medium text-slate-800 m-0 mt-0.5 break-words">{value}</p>
    </div>
  );
}

function Shell({ title, subtitle, onClose, children, zIndex }) {
  return (
    <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ zIndex }}>
      <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-900/45" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 bg-slate-50">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 m-0 truncate">{title}</h2>
            {subtitle ? <p className="text-xs text-slate-500 m-0 mt-0.5">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3">{children}</div>
      </div>
    </div>
  );
}

function ShipmentBody({ shipment, onOpenMl }) {
  const when = formatWhen(shipment.created_at || shipment.booking_date);
  const from = shipment.sender_location || locationName(shipment.sender_location_hub);
  const to = shipment.receiver_location || locationName(shipment.receiver_location_hub);
  const charges = CHARGE_FIELDS.filter(([key]) => hasAmount(shipment[key]));
  return (
    <div className="space-y-4">
      {hasText(shipment.ml_sheet_id) && (
        <button
          type="button"
          onClick={() => onOpenMl(shipment.ml_sheet_id)}
          className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-100"
        >
          ML Sheet
          <span className="underline underline-offset-2">{shipment.ml_sheet_id}</span>
        </button>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="From" value={from} />
        <Field label="To" value={to} />
        {hasText(when) ? <Field label="Booked" value={when} /> : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-slate-200 p-3">
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 m-0">Sender</p>
          <Field label="Name" value={shipment.sender_name} />
          <Field label="Contact" value={shipment.sender_contact} />
          <Field label="Address" value={shipment.sender_address} />
          <Field label="GST" value={shipment.sender_gst} />
          <Field label="Email" value={shipment.sender_email} />
        </div>
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 m-0">Receiver</p>
          <Field label="Name" value={shipment.receiver_name} />
          <Field label="Contact" value={shipment.receiver_contact} />
          <Field label="Address" value={shipment.receiver_address} />
          <Field label="GST" value={shipment.receiver_gst} />
          <Field label="Email" value={shipment.receiver_email} />
        </div>
      </div>
      {charges.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 text-xs font-bold text-slate-800 bg-sky-100">Charges</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3">
            {charges.map(([key, label]) => (
              <div key={key} className="min-w-0">
                <p className="text-[10px] font-semibold text-slate-400 m-0">{label}</p>
                <p className="text-sm font-bold text-slate-800 m-0">{money(shipment[key])}</p>
              </div>
            ))}
          </div>
          {hasAmount(shipment.total_amount) && (
            <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-3 py-2">
              <span className="text-xs font-bold text-slate-600">Total</span>
              <span className="text-sm font-extrabold text-slate-900">{money(shipment.total_amount)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function useShipment(id) {
  const [state, setState] = useState({ loading: true, error: '', shipment: null });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: '', shipment: null });
    shipmentService.getShipment(id)
      .then((res) => {
        if (cancelled) return;
        const shipment = res?.data?.shipment || res?.data?.data || res?.data;
        if (!shipment?.shipment_id && !shipment?.sender_name) {
          setState({ loading: false, error: 'Shipment not found', shipment: null });
          return;
        }
        setState({ loading: false, error: '', shipment });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: err?.response?.data?.error || 'Could not load shipment',
          shipment: null,
        });
      });
    return () => { cancelled = true; };
  }, [id]);
  return state;
}

function useMlSheet(id) {
  const [state, setState] = useState({ loading: true, error: '', sheet: null, shipments: [], summary: {} });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: '', sheet: null, shipments: [], summary: {} });
    mlSheetService.getMLSheet(id)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        setState({
          loading: false,
          error: '',
          sheet: data.ml_sheet || data.data || null,
          shipments: Array.isArray(data.shipments) ? data.shipments : [],
          summary: data.summary || {},
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: err?.response?.data?.error || 'Could not load ML sheet',
          sheet: null,
          shipments: [],
          summary: {},
        });
      });
    return () => { cancelled = true; };
  }, [id]);
  return state;
}

function MlBody({ sheet, shipments, summary, onOpenShipment }) {
  const chargeCols = useMemo(() => {
    return CHARGE_FIELDS.filter(([key]) => shipments.some((row) => hasAmount(row[key])));
  }, [shipments]);
  const totals = useMemo(() => {
    const out = { paid: 0, to_pay: 0, on_account: 0, total: 0, packages: 0 };
    chargeCols.forEach(([key]) => { out[key] = 0; });
    shipments.forEach((row) => {
      const bucket = paymentBucket(row);
      const total = Number(row.total_amount || 0);
      out[bucket] += total;
      out.total += total;
      out.packages += Number(row.no_of_articles || 0);
      chargeCols.forEach(([key]) => { out[key] += Number(row[key] || 0); });
    });
    return out;
  }, [shipments, chargeCols]);

  const from = (sheet?.from_locations || []).map((item) => item.location || item.name).filter(Boolean).join(', ');
  const to = (sheet?.to_locations || []).map((item) => item.location || item.name).filter(Boolean).join(', ');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ['Shipments', summary.total_shipments ?? shipments.length],
          ['Paid', money(totals.paid)],
          ['To Pay', money(totals.to_pay)],
          ['On Account', money(totals.on_account)],
          ['Total', money(summary.total_amount ?? totals.total)],
        ].filter(([, value]) => hasText(value) && value !== '₹0').map(([label, value]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
            <p className="text-[10px] font-semibold uppercase text-slate-400 m-0">{label}</p>
            <p className="text-sm font-extrabold text-slate-900 m-0 mt-0.5">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From" value={from} />
        <Field label="To" value={to} />
        <Field label="Vehicle" value={sheet?.vehicle_number} />
        <Field label="Status" value={sheet?.status} />
        <Field label="Created" value={formatWhen(sheet?.created_at)} />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full border-collapse text-xs min-w-[640px]">
          <thead>
            <tr className="bg-sky-100 text-slate-800">
              <th className="text-left px-2 py-2 font-bold">Shipment</th>
              <th className="text-left px-2 py-2 font-bold">From</th>
              <th className="text-left px-2 py-2 font-bold">To</th>
              {chargeCols.map(([key, label]) => (
                <th key={key} className="text-right px-2 py-2 font-bold whitespace-nowrap">{label}</th>
              ))}
              <th className="text-right px-2 py-2 font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((row) => (
              <tr key={row.shipment_id} className="border-t border-slate-100">
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    className="font-semibold text-blue-700 underline underline-offset-2"
                    onClick={() => onOpenShipment(row.shipment_id)}
                  >
                    {row.shipment_id}
                  </button>
                </td>
                <td className="px-2 py-1.5">{row.sender_location || '—'}</td>
                <td className="px-2 py-1.5">{row.receiver_location || '—'}</td>
                {chargeCols.map(([key]) => (
                  <td key={key} className="px-2 py-1.5 text-right tabular-nums">
                    {hasAmount(row[key]) ? money(row[key]) : '—'}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                  {hasAmount(row.total_amount) ? money(row.total_amount) : '—'}
                </td>
              </tr>
            ))}
            {shipments.length > 0 && (
              <tr className="border-t border-sky-200 bg-sky-50 font-bold">
                <td className="px-2 py-2" colSpan={3}>Total</td>
                {chargeCols.map(([key]) => (
                  <td key={key} className="px-2 py-2 text-right tabular-nums">{money(totals[key])}</td>
                ))}
                <td className="px-2 py-2 text-right tabular-nums">{money(totals.total)}</td>
              </tr>
            )}
            {shipments.length === 0 && (
              <tr>
                <td colSpan={3 + chargeCols.length + 1} className="px-3 py-6 text-center text-slate-400">
                  No shipments on this ML sheet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShipmentLayer({ id, onClose, onOpenMl, zIndex }) {
  const { loading, error, shipment } = useShipment(id);
  const when = formatWhen(shipment?.created_at);
  return (
    <Shell
      zIndex={zIndex}
      onClose={onClose}
      title={shipment?.shipment_id || id}
      subtitle={when || (loading ? 'Loading shipment' : '')}
    >
      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading shipment
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {shipment && <ShipmentBody shipment={shipment} onOpenMl={onOpenMl} />}
    </Shell>
  );
}

function MlLayer({ id, onClose, onOpenShipment, zIndex }) {
  const { loading, error, sheet, shipments, summary } = useMlSheet(id);
  return (
    <Shell
      zIndex={zIndex}
      onClose={onClose}
      title={sheet?.ml_sheet_id || id}
      subtitle="ML sheet"
    >
      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading ML sheet
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {sheet && (
        <MlBody
          sheet={sheet}
          shipments={shipments}
          summary={summary}
          onOpenShipment={onOpenShipment}
        />
      )}
    </Shell>
  );
}

export function ReportPreviewStack({ stack, onOpenShipment, onOpenMl, onClose }) {
  if (!stack?.length) return null;
  return stack.map((item, index) => {
    const zIndex = 80 + index * 10;
    const close = () => onClose(index);
    if (item.type === 'ml') {
      return (
        <MlLayer
          key={`ml-${item.id}-${index}`}
          id={item.id}
          zIndex={zIndex}
          onClose={close}
          onOpenShipment={onOpenShipment}
        />
      );
    }
    return (
      <ShipmentLayer
        key={`shipment-${item.id}-${index}`}
        id={item.id}
        zIndex={zIndex}
        onClose={close}
        onOpenMl={onOpenMl}
      />
    );
  });
}
